import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Chart from "../../utils/chart";

const COLORS = ['#007bff', '#ff4169', '#17c671', '#8445f7', '#fb7906'];

const hexToRgb = (value) => {
    let hex = value || '#000000';
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (match, red, green, blue) =>
        red + red + green + green + blue + blue
    );
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
    } : { r: 0, g: 0, b: 0 };
};

const getLineOptions = overrides => ({
    responsive: true,
    legend: {
        position: "top"
    },
    elements: {
        line: {
            tension: 0.3
        },
        point: {
            radius: 0
        }
    },
    scales: {
        xAxes: [
            {
                gridLines: false,
                ticks: {
                    callback(tick, index) {
                        return index % 2 !== 0 ? "" : tick;
                    }
                }
            }
        ],
        yAxes: [
            {
                ticks: {
                    suggestedMax: 45,
                    callback(tick) {
                        if (tick === 0) return tick;
                        return tick > 999 ? `${(tick / 1000).toFixed(1)}K` : tick;
                    }
                }
            }
        ]
    },
    hover: {
        mode: "nearest",
        intersect: false
    },
    tooltips: {
        custom: false,
        mode: "index",
        intersect: false
    },
    ...overrides
});

const getPieOptions = overrides => ({
    responsive: true,
    legend: {
        position: "right",
        labels: {
            padding: 7,
            boxWidth: 20
        }
    },
    cutoutPercentage: 0,
    tooltips: {
        custom: false,
        mode: "index",
        position: "nearest"
    },
    ...overrides
});

const VChart = (props) => {
    const canvasRef = useRef();
    const chartRef = useRef();

    useEffect(() => {
        if (chartRef.current) {
            chartRef.current.destroy();
            chartRef.current = null;
        }

        if (!canvasRef.current || !Array.isArray(props.datasets)) {
            return undefined;
        }

        let datasets = [];
        if (props.type === 'pie') {
            const source = props.datasets[0];
            const data = source && Array.isArray(source.data) ? source.data : [];
            if (data.length === 0) return undefined;

            const backgroundColor = Array.isArray(source.backgroundColor)
                ? source.backgroundColor
                : data.map((item, index) => {
                    const color = hexToRgb(COLORS[index % COLORS.length]);
                    const alpha = Math.max(
                        1 - (Math.floor(index / COLORS.length) * 0.3),
                        0.2
                    );
                    return `rgba(${color.r},${color.g},${color.b},${alpha})`;
                });
            datasets = [{
                data,
                backgroundColor,
                borderWidth: 0.5,
            }];
        } else {
            datasets = props.datasets.map((dataset, index) => {
                const color = hexToRgb(dataset.color || COLORS[index % COLORS.length]);
                return {
                    label: dataset.label === undefined ? index : dataset.label,
                    fill: dataset.fill || '',
                    data: dataset.data || [],
                    backgroundColor: `rgba(${color.r},${color.g},${color.b},0.1)`,
                    borderColor: `rgba(${color.r},${color.g},${color.b},1)`,
                    pointBackgroundColor: dataset.pointBackgroundColor || "rgba(255,255,255,1)",
                    pointHoverBackgroundColor: `rgba(${color.r},${color.g},${color.b},1)`,
                    borderWidth: dataset.borderWidth === undefined ? 1.5 : dataset.borderWidth,
                    pointRadius: dataset.pointRadius === undefined ? 0 : dataset.pointRadius,
                    pointHoverRadius: dataset.pointHoverRadius === undefined
                        ? 3
                        : dataset.pointHoverRadius,
                    borderDash: dataset.borderDash || [0, 0],
                    order: dataset.order === undefined ? 1 : dataset.order,
                    xAxisID: dataset.xAxisID,
                    yAxisID: dataset.yAxisID,
                    type: dataset.type,
                };
            });
        }

        if (datasets.length === 0) return undefined;

        chartRef.current = new Chart(canvasRef.current, {
            type: props.type || 'LineWithLine',
            data: {
                labels: Array.isArray(props.labels) ? props.labels : [],
                datasets,
            },
            options: props.type === 'pie'
                ? getPieOptions(props.chartOptions)
                : getLineOptions(props.chartOptions),
        });

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [props.chartOptions, props.datasets, props.labels, props.type]);

    return (
        <canvas
            height={props.height || '100'}
            ref={canvasRef}
            style={{ ...props.style, maxWidth: "100%" }}
            className={props.className}
        />
    );
};

VChart.propTypes = {
    title: PropTypes.string,
    labels: PropTypes.array,
    datasets: PropTypes.array,
    chartOptions: PropTypes.object,
    type: PropTypes.string,
    height: PropTypes.string,
    style: PropTypes.object,
    className: PropTypes.string,
};

VChart.defaultProps = {
    labels: [1, 2, 3],
    datasets: [
        {
            label: `Current Period`,
            data: [10, 20, 30],
        },
        {
            label: `Past Period`,
            data: [20, 10, 30],
            borderDash: [3, 3],
        }
    ]
};

export default VChart;
